import React, { useEffect, useState } from "react";
import { PRODUCTS } from "../lib/products";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { EnquiryDialog } from "../components/EnquiryDialog";
import { Button } from "../components/ui/button";
import { buildWhatsAppUrl } from "../lib/whatsapp";
import { ShieldCheck, Lightbulb, Users, IndianRupee, ArrowRight, Sparkles } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1529180979161-06b8b6d6f2be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const ABOUT_IMG = "https://images.pexels.com/photos/8112186/pexels-photo-8112186.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function HomePage() {
  const [whatsapp, setWhatsapp] = useState("919315542583");
  const [googleFormUrl, setGoogleFormUrl] = useState("");
  const [activeProduct, setActiveProduct] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => {
      setWhatsapp(r.data.whatsapp_number);
      setGoogleFormUrl(r.data.google_form_url || "");
    }).catch(() => {});
  }, []);

  const openProduct = (p) => { setActiveProduct(p); setOpen(true); };

  const pillars = [
    { icon: ShieldCheck, label: "Trusted Advice" },
    { icon: Lightbulb, label: "Smart Solutions" },
    { icon: Users, label: "Customer First" },
    { icon: IndianRupee, label: "Value That Matters" },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#0B192C]">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#E6F0ED] px-4 py-1.5 text-xs uppercase tracking-[0.22em] font-bold text-[#006A4E]">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} /> Ek Hi Jagah, Sabhi Suraksha!
            </div>
            <h1 className="font-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-bold text-[#0B192C]">
              Secure <span className="italic text-[#006A4E]">today</span>,<br /> Secure tomorrow.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-[#4A5568] max-w-xl">
              Compare leading insurers, get the best premium quotes and renew in minutes — across Life,
              Health, Motor, Travel, Home & Business. All in one place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-cta-quote-btn"
                onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full bg-[#006A4E] hover:bg-[#00523C] text-white px-8 py-6 font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                Get a Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a
                data-testid="hero-cta-whatsapp"
                href={buildWhatsAppUrl(whatsapp, "Hi MY BEEMA, I'd like to talk to an advisor.")}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center rounded-full border border-[#0B192C]/20 hover:border-[#006A4E] text-[#0B192C] hover:text-[#006A4E] px-8 py-3 font-semibold transition-all"
              >
                Talk to an Advisor
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[#4A5568]">
              {pillars.map(({ icon: Ic, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <Ic className="w-4 h-4 text-[#006A4E]" strokeWidth={1.5} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative fade-up" style={{ animationDelay: ".15s" }}>
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-[#006A4E]/15 via-transparent to-[#0B192C]/10 blur-2xl" aria-hidden />
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-black/5 shadow-[0_30px_80px_-30px_rgba(11,25,44,0.35)]">
              <img src={HERO_IMG} alt="Family at sunset" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="font-display italic text-2xl">"One stop. All protection."</div>
                <div className="text-xs uppercase tracking-[0.25em] text-white/70 mt-2">Trusted by 10,000+ families</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 lg:py-24">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#006A4E]">Our Products</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight font-bold mt-3 text-[#0B192C]">
                Ek Hi Jagah, <span className="italic text-[#006A4E]">Sabhi Suraksha</span>.
              </h2>
              <p className="mt-3 text-[#4A5568] max-w-2xl">Click any product to share a few details — we'll send you a tailored quote on WhatsApp.</p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {PRODUCTS.map((p) => (
              <button
                key={p.id}
                data-testid={`product-card-${p.id}`}
                onClick={() => openProduct(p)}
                className="product-card group text-left bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:border-[#006A4E]/40 hover:shadow-[0_18px_40px_-20px_rgba(11,25,44,0.25)] hover:-translate-y-1 transition-all"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={p.image} alt={p.title} className="product-card-img w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <div className="font-display text-lg leading-tight">{p.title}</div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#006A4E] font-semibold">{p.tagline}</div>
                  <p className="mt-2 text-sm text-[#4A5568] line-clamp-2">{p.description}</p>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-[#0B192C] group-hover:text-[#006A4E] transition-colors">
                    Enquire now <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-[#006A4E]/10 to-transparent blur-2xl" />
            <img src={ABOUT_IMG} alt="Trust" className="relative rounded-[2rem] w-full h-[460px] object-cover shadow-[0_30px_80px_-30px_rgba(11,25,44,0.35)]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#006A4E]">Why MY BEEMA</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight font-bold mt-3 text-[#0B192C]">
              Trusted advisors. <br /> <span className="italic text-[#006A4E]">Honest advice.</span>
            </h2>
            <p className="mt-4 text-[#4A5568] leading-relaxed max-w-xl">
              We don't sell insurance — we help you choose it. Backed by India's leading insurers, our
              advisors compare policies side-by-side and recommend what's right for you.
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-5">
              {pillars.map(({ icon: Ic, label }) => (
                <li key={label} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-[#E2E8F0]">
                  <div className="w-10 h-10 rounded-lg bg-[#E6F0ED] flex items-center justify-center">
                    <Ic className="w-5 h-5 text-[#006A4E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B192C]">{label}</div>
                    <div className="text-xs text-[#4A5568] mt-0.5">Built into every quote.</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer whatsappNumber={whatsapp} />
      <WhatsAppFloat number={whatsapp} googleFormUrl={googleFormUrl} />

      <EnquiryDialog
        product={activeProduct}
        open={open}
        onOpenChange={setOpen}
        whatsappNumber={whatsapp}
        googleFormUrl={googleFormUrl}
      />
    </div>
  );
}
